// Design system: notebook. Paper by day, chalkboard by night. See CoreDesignPrinciples.md.
// All values must stay 6-digit hex: call sites append 2-digit alpha (`${colors.x}20`).
// The accent IS the ink: filled controls use `accent` with `onAccent` text.

export const lightColors = {
  bg: '#f7f5ef',
  bgSecondary: '#f3f1eb',
  bgTertiary: '#ebe8e1',
  border: '#dcd8cf',
  borderLight: '#e6e3dc',
  borderStrong: '#8b877e',

  textPrimary: '#141412',
  textSecondary: '#5c5953',
  textMuted: '#6b675f',

  accent: '#141412',
  onAccent: '#f7f5ef',
  success: '#141412',
  info: '#5c5953',
  warning: '#141412',
  error: '#a33a2c',
};

export const darkColors = {
  bg: '#0f1110',
  bgSecondary: '#131514',
  bgTertiary: '#1a1c1b',
  border: '#292b2a',
  borderLight: '#202221',
  borderStrong: '#5d605e',

  textPrimary: '#e8e7e1',
  textSecondary: '#a3a49f',
  textMuted: '#8f908b',

  accent: '#e8e7e1',
  onAccent: '#0f1110',
  success: '#e8e7e1',
  info: '#a3a49f',
  warning: '#e8e7e1',
  error: '#e0806f',
};

export const getColors = (isDark) => isDark ? darkColors : lightColors;

export const fonts = {
  serif: "'Noto Serif', Georgia, serif",
  sans: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  mono: "'Geist Mono', ui-monospace, 'SF Mono', Menlo, monospace",
};

// Date utilities
export const getCurrentMonth = () => new Date().getMonth();
export const getCurrentYear = () => new Date().getFullYear();
export const getToday = () => new Date().getDate();
export const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
export const getMonthName = (month) => {
  const months = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'
  ];
  return months[month];
};

// Format date as YYYY-MM-DD string
export const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get all days in a month for calendar display
export const getCalendarDays = (year, month) => {
  const daysInMonth = getDaysInMonth(year, month);
  const days = [];
  
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    days.push({
      date,
      dateString: formatDate(date),
      dayOfWeek: date.getDay(),
      isWeekend: date.getDay() === 0 || date.getDay() === 6
    });
  }
  
  return days;
};

// Time of day options
export const timeOfDayOptions = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
  { value: 'night', label: 'Night' },
  { value: 'anytime', label: 'Anytime' },
];

// Task categories
export const taskCategories = [
  { value: 'work', label: 'Work', color: '#6b675f' },
  { value: 'personal', label: 'Personal', color: '#6b675f' },
  { value: 'health', label: 'Health', color: '#6b675f' },
  { value: 'finance', label: 'Finance', color: '#6b675f' },
  { value: 'learning', label: 'Learning', color: '#6b675f' },
  { value: 'other', label: 'Other', color: '#6b675f' },
];

// Weight/Priority options
export const weightOptions = [
  { value: 'low', label: 'Low', color: '#8b877e' },
  { value: 'medium', label: 'Medium', color: '#6b675f' },
  { value: 'high', label: 'High', color: '#3a3833' },
  { value: 'critical', label: 'Critical', color: '#a33a2c' },
];

// Habit templates for onboarding
export const HABIT_TEMPLATES = [
  {
    id: 'wellness',
    name: 'Wellness Warrior',
    description: 'Build a foundation for health & fitness',
    habits: [
      { name: "Morning workout (30 min)", timeOfDay: 'morning', goal: 30 },
      { name: "Drink 8 glasses of water", timeOfDay: 'anytime', goal: 30 },
      { name: "Take vitamins/supplements", timeOfDay: 'morning', goal: 30 },
      { name: "Eat a balanced meal", timeOfDay: 'anytime', goal: 30 },
      { name: "Get 7+ hours of sleep", timeOfDay: 'evening', goal: 30 },
    ]
  },
  {
    id: 'productivity',
    name: 'Productivity Pro',
    description: 'Maximize focus and get things done',
    habits: [
      { name: "Review daily priorities", timeOfDay: 'morning', goal: 30 },
      { name: "Deep work session (2 hrs)", timeOfDay: 'morning', goal: 30 },
      { name: "Inbox zero", timeOfDay: 'afternoon', goal: 30 },
      { name: "Learn something new (15 min)", timeOfDay: 'anytime', goal: 30 },
      { name: "Plan tomorrow's tasks", timeOfDay: 'evening', goal: 30 },
    ]
  },
  {
    id: 'mindful',
    name: 'Mindful Living',
    description: 'Find balance and inner peace',
    habits: [
      { name: "Morning meditation (10 min)", timeOfDay: 'morning', goal: 30 },
      { name: "Gratitude journaling", timeOfDay: 'morning', goal: 30 },
      { name: "Connect with loved ones", timeOfDay: 'evening', goal: 30 },
      { name: "Digital detox (1 hour)", timeOfDay: 'evening', goal: 30 },
      { name: "Evening reflection", timeOfDay: 'evening', goal: 30 },
    ]
  },
  {
    id: 'growth',
    name: 'Growth Mindset',
    description: 'Never stop learning and growing',
    habits: [
      { name: "Read (30 min)", timeOfDay: 'morning', goal: 30 },
      { name: "Practice a new skill (30 min)", timeOfDay: 'anytime', goal: 30 },
      { name: "Write or journal", timeOfDay: 'morning', goal: 30 },
      { name: "Listen to educational content", timeOfDay: 'anytime', goal: 30 },
      { name: "Reflect on lessons learned", timeOfDay: 'evening', goal: 30 },
    ]
  }
];

// Task templates for onboarding - common adult errands and chores
export const TASK_TEMPLATES = [
  {
    id: 'household',
    name: 'Household Essentials',
    description: 'Keep your home running smoothly',
    tasks: [
      { title: "Do laundry", priority: 'medium', category: 'chores' },
      { title: "Deep clean kitchen", priority: 'medium', category: 'chores' },
      { title: "Grocery shopping", priority: 'high', category: 'errands' },
      { title: "Take out trash/recycling", priority: 'medium', category: 'chores' },
      { title: "Organize closet", priority: 'low', category: 'chores' },
    ]
  },
  {
    id: 'health',
    name: 'Health & Wellness',
    description: 'Stay on top of health appointments',
    tasks: [
      { title: "Schedule dentist appointment", priority: 'medium', category: 'health' },
      { title: "Schedule eye exam", priority: 'medium', category: 'health' },
      { title: "Schedule annual physical", priority: 'medium', category: 'health' },
      { title: "Refill prescriptions", priority: 'high', category: 'health' },
      { title: "Sign up for gym/fitness class", priority: 'low', category: 'health' },
    ]
  },
  {
    id: 'finance',
    name: 'Financial Tasks',
    description: 'Keep your finances in order',
    tasks: [
      { title: "File taxes", priority: 'high', category: 'finance' },
      { title: "Pay credit card bills", priority: 'high', category: 'finance' },
      { title: "Review monthly budget", priority: 'medium', category: 'finance' },
      { title: "Update savings contributions", priority: 'low', category: 'finance' },
      { title: "Review subscriptions", priority: 'low', category: 'finance' },
    ]
  },
  {
    id: 'family',
    name: 'Family & Relationships',
    description: 'Nurture your important relationships',
    tasks: [
      { title: "Call parents", priority: 'medium', category: 'family' },
      { title: "Plan birthday gift for ___", priority: 'medium', category: 'family' },
      { title: "Schedule family dinner", priority: 'low', category: 'family' },
      { title: "Send thank you card", priority: 'low', category: 'family' },
      { title: "Organize family photos", priority: 'low', category: 'family' },
    ]
  },
  {
    id: 'pets',
    name: 'Pet Care',
    description: 'Keep your furry friends happy',
    tasks: [
      { title: "Schedule pet grooming", priority: 'medium', category: 'pets' },
      { title: "Schedule vet checkup", priority: 'medium', category: 'pets' },
      { title: "Buy pet food/supplies", priority: 'high', category: 'pets' },
      { title: "Refill pet medications", priority: 'high', category: 'pets' },
      { title: "Update pet tags/microchip", priority: 'low', category: 'pets' },
    ]
  },
  {
    id: 'car',
    name: 'Car & Transportation',
    description: 'Keep your vehicle running',
    tasks: [
      { title: "Schedule oil change", priority: 'medium', category: 'car' },
      { title: "Renew car registration", priority: 'high', category: 'car' },
      { title: "Review car insurance", priority: 'low', category: 'car' },
      { title: "Get car washed", priority: 'low', category: 'car' },
      { title: "Check tire pressure", priority: 'medium', category: 'car' },
    ]
  }
];

// Global Modal Style
export const getModalStyle = (colors) => ({
  backgroundColor: colors.bg,
  border: `1px solid ${colors.textPrimary}`,
});

